import React, { useMemo, useState, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { useDealBlur } from '@/finsight/ui/components/deals/useDealBlur';
import Detail from './Detail';
import {
  getDealFilings,
  getDealPropertyGroups,
  mapTrancheComments,
  renderPropertyGroups,
} from './helpers';
import IDeal from '@/finsight/domain/vo/IDeal';
import styles from './dealDetails.scss';

interface IDealDetailsProps {
  deal: IDeal,
  showDealDetailsByDefault: boolean,
}

const DealDetails = ({ deal, showDealDetailsByDefault = false }: IDealDetailsProps) => {
  const { isBlurred } = useDealBlur();
  const dealDetailsRef = useRef(null);
  const [isDetailsBlockVisible, setIsDetailsBlockVisible] = useState(showDealDetailsByDefault && !isBlurred);
  const [detailsHeight, setDetailsHeight] = useState(0);

  useEffect(() => {
    const calculateDetailsHeight = debounce(() => {
      if (!dealDetailsRef.current) {
        return;
      }
      const height = isDetailsBlockVisible ? dealDetailsRef.current.offsetHeight : 0;
      setDetailsHeight(height);
    }, 100);

    calculateDetailsHeight();
    if (isDetailsBlockVisible) {
      window.addEventListener('resize', calculateDetailsHeight);
    }

    return () => {
      window.removeEventListener('resize', calculateDetailsHeight);
    };
  }, [isDetailsBlockVisible]);

  const filings = useMemo(
    () => getDealFilings(deal),
    [deal],
  );

  const trancheComments = useMemo(
    () => deal.tranches
          && deal.tranches.length
          && deal.tranches.map(mapTrancheComments)
            .filter((item) => !!item)
            .join('\n'),
    [deal.tranches],
  );

  const dealPropertyGroups = useMemo(
    () => getDealPropertyGroups(deal),
    [deal],
  );

  const showDetailsClickHandler = () => {
    setIsDetailsBlockVisible(!isDetailsBlockVisible);
  };

  const renderShowHideButton = () => (
    <div
      className={ styles.showHideContainer }
      onClick={ showDetailsClickHandler }
    >
      <a className={ styles.showHideBtn } data-test={ `${ isDetailsBlockVisible ? 'hide' : 'more' }Details` }>
        { isDetailsBlockVisible ? 'Hide' : 'More' } Details
      </a>
    </div>
  );

  const dealHasDetails = [
    filings,
    trancheComments,
    deal.comments,
    deal.collateral,
    deal.guarantor,
    deal.originator,
    deal.riskRetention,
    deal.useOfProceeds,
    deal.volckerCompliance,
    dealPropertyGroups.length > 0,
  ].some((item) => !!item);

  const isDetailsVisible = !isBlurred && dealHasDetails;

  return (
    <>
      { isDetailsVisible && renderShowHideButton() }

      <div
        className={ styles.detailsContainer }
        style={ { height: detailsHeight } }
      >
        <div
          ref={ dealDetailsRef }
          className={ styles.detailsHolder }
        >
          <Detail
            label="Filings"
            value={ filings }
          />
          <Detail
            label="Tranche Comments"
            value={ trancheComments }
          />
          <Detail
            label="Deal Comments"
            value={ deal.comments }
          />
          <Detail
            label="Collateral"
            value={ deal.collateral }
          />
          <Detail
            label="Guarantor(s)"
            value={ deal.guarantor }
          />
          <Detail
            label="Originator(s)"
            value={ deal.originator }
          />
          <Detail
            label="Risk Retention"
            value={ deal.riskRetention }
          />
          <Detail
            label="Use of Proceeds"
            value={ deal.useOfProceeds }
          />
          <Detail
            label="Volcker Compliance"
            value={ deal.volckerCompliance }
          />

          { renderPropertyGroups(dealPropertyGroups) }
        </div>
      </div>
    </>
  );
};

export default React.memo(DealDetails);
